import { dashboardApi } from '../dashboard-api';
import { GetBoughtPhoneNumbersResponse } from 'src/slices/solve-config/types';
import {
  AddStepRequest,
  AnalyticsRequest,
  AnalyticsResponse,
  CopyStepsResponse,
  CreateWorkflowRequest,
  CreateWorkflowResponse,
  OrchestratorTriageConfig,
  OrchestratorWorkflow,
  PasteStepsRequest,
  SetTriggerConfigRequest,
  UpdateStepRequest,
  UpdateWorkflowRequest,
  ValidationResponse,
  WorkflowListResponse,
  WorkflowSummary,
} from 'src/types/orchestrator';

const BASE_URL = 'dashboard-controls/orchestrator';
const SOLVE_CONFIG_BASE_URL = 'dashboard-controls/solve/config';

export interface SetActiveRequest {
  is_active: boolean;
}

const orchestratorApi = dashboardApi.injectEndpoints({
  endpoints: builder => ({
    addInternalStep: builder.mutation<
      OrchestratorWorkflow,
      { request: AddStepRequest; version: number; workflowId: string }
    >({
      invalidatesTags: ['OrchestratorWorkflow'],
      query: ({ request, version, workflowId }) => ({
        body: request,
        method: 'POST',
        url: `${BASE_URL}/workflows/${workflowId}/version/${version}/internal-step`,
      }),
    }),
    addStep: builder.mutation<
      OrchestratorWorkflow,
      { request: AddStepRequest; version: number; workflowId: string }
    >({
      invalidatesTags: ['OrchestratorWorkflow'],
      query: ({ request, version, workflowId }) => ({
        body: request,
        method: 'POST',
        url: `${BASE_URL}/workflows/${workflowId}/version/${version}/step`,
      }),
    }),
    archiveWorkflow: builder.mutation<{ status: string }, string>({
      invalidatesTags: ['OrchestratorWorkflow'],
      query: workflowId => ({
        method: 'DELETE',
        url: `${BASE_URL}/workflows/${workflowId}`,
      }),
    }),
    cloneWorkflow: builder.mutation<CreateWorkflowResponse, string>({
      invalidatesTags: ['OrchestratorWorkflow'],
      query: workflowId => ({
        method: 'POST',
        url: `${BASE_URL}/workflows/${workflowId}/clone`,
      }),
    }),
    copySteps: builder.mutation<
      CopyStepsResponse,
      { stepId: string; version: number; workflowId: string }
    >({
      query: ({ stepId, version, workflowId }) => ({
        method: 'POST',
        url: `${BASE_URL}/workflows/${workflowId}/version/${version}/step/${stepId}/copy`,
      }),
    }),
    createWorkflow: builder.mutation<
      CreateWorkflowResponse,
      CreateWorkflowRequest
    >({
      invalidatesTags: ['OrchestratorWorkflow'],
      query: body => ({
        body,
        method: 'POST',
        url: `${BASE_URL}/workflows`,
      }),
    }),
    deleteInternalStep: builder.mutation<
      OrchestratorWorkflow,
      {
        loopStepId: string;
        stepId: string;
        version: number;
        workflowId: string;
      }
    >({
      invalidatesTags: ['OrchestratorWorkflow'],
      query: ({ loopStepId, stepId, version, workflowId }) => ({
        method: 'DELETE',
        url: `${BASE_URL}/workflows/${workflowId}/version/${version}/internal-step/${stepId}?loop_step_id=${loopStepId}`,
      }),
    }),
    deleteStep: builder.mutation<
      OrchestratorWorkflow,
      {
        internalStepIds?: string[];
        stepId: string;
        version: number;
        workflowId: string;
      }
    >({
      invalidatesTags: ['OrchestratorWorkflow'],
      query: ({ internalStepIds, stepId, version, workflowId }) => {
        const params = new URLSearchParams();
        if (internalStepIds && internalStepIds.length > 0) {
          params.set('internal_step_ids', internalStepIds.join(','));
        }
        const queryString = params.toString();
        const url = `${BASE_URL}/workflows/${workflowId}/version/${version}/step/${stepId}${
          queryString ? `?${queryString}` : ''
        }`;
        return {
          method: 'DELETE',
          url,
        };
      },
    }),
    discardDraft: builder.mutation<
      OrchestratorWorkflow,
      { version: number; workflowId: string }
    >({
      invalidatesTags: [
        'OrchestratorWorkflow',
        'OrchestratorAutoflowWorkflow',
        'OrchestratorAutoflowWorkflowNames',
      ],
      query: ({ version, workflowId }) => ({
        method: 'POST',
        url: `${BASE_URL}/workflows/${workflowId}/version/${version}/discard`,
      }),
    }),
    getOrchestratorAnalytics: builder.query<
      AnalyticsResponse,
      AnalyticsRequest
    >({
      query: params => ({
        method: 'GET',
        params: {
          end_date: params.end_date,
          previous_end_date: params.previous_end_date,
          previous_start_date: params.previous_start_date,
          start_date: params.start_date,
        },
        url: `${BASE_URL}/analytics`,
      }),
    }),
    getOrchestratorApiToken: builder.query<{ api_key: string }, void>({
      query: () => ({
        method: 'GET',
        url: `${BASE_URL}/api-token`,
      }),
    }),
    getOrchestratorTriageConfigs: builder.query<
      OrchestratorTriageConfig[],
      void
    >({
      query: () => ({
        method: 'GET',
        url: `${BASE_URL}/triage-configs`,
      }),
    }),
    getRedactionConfig: builder.query<
      {
        enabled: boolean;
        persistence_hours: number;
        time_to_redact_hours: number;
      },
      { channel: string }
    >({
      query: ({ channel }) => ({
        url: `${BASE_URL}/redaction-config?channel=${channel}`,
      }),
    }),
    getVoicePhoneNumbers: builder.query<GetBoughtPhoneNumbersResponse, void>({
      query: () => ({
        method: 'GET',
        url: `${SOLVE_CONFIG_BASE_URL}/voice/bought-phone-numbers`,
      }),
    }),
    getWorkflow: builder.query<
      OrchestratorWorkflow,
      { version?: number; workflowId: string }
    >({
      providesTags: ['OrchestratorWorkflow'],
      query: ({ version, workflowId }) => ({
        method: 'GET',
        params: version ? { version } : undefined,
        url: `${BASE_URL}/workflows/${workflowId}`,
      }),
    }),
    listWorkflows: builder.query<WorkflowListResponse, void>({
      providesTags: ['OrchestratorWorkflow'],
      query: () => ({
        method: 'GET',
        url: `${BASE_URL}/workflows`,
      }),
    }),
    pasteSteps: builder.mutation<
      OrchestratorWorkflow,
      { request: PasteStepsRequest; version: number; workflowId: string }
    >({
      invalidatesTags: ['OrchestratorWorkflow'],
      query: ({ request, version, workflowId }) => ({
        body: request,
        method: 'POST',
        url: `${BASE_URL}/workflows/${workflowId}/version/${version}/steps/paste`,
      }),
    }),
    publishWorkflow: builder.mutation<
      OrchestratorWorkflow,
      { version: number; workflowId: string }
    >({
      invalidatesTags: ['OrchestratorWorkflow'],
      query: ({ version, workflowId }) => ({
        method: 'POST',
        url: `${BASE_URL}/workflows/${workflowId}/version/${version}/publish`,
      }),
    }),
    setWorkflowActive: builder.mutation<
      WorkflowSummary,
      { request: SetActiveRequest; workflowId: string }
    >({
      invalidatesTags: ['OrchestratorWorkflow'],
      query: ({ request, workflowId }) => ({
        body: request,
        method: 'PUT',
        url: `${BASE_URL}/workflows/${workflowId}/active`,
      }),
    }),
    updateOrchestratorWorkflow: builder.mutation<
      { status: string },
      { request: UpdateWorkflowRequest; workflowId: string }
    >({
      invalidatesTags: ['OrchestratorWorkflow'],
      query: ({ request, workflowId }) => ({
        body: request,
        method: 'PUT',
        url: `${BASE_URL}/workflows/${workflowId}`,
      }),
    }),
    updateStep: builder.mutation<
      OrchestratorWorkflow,
      {
        request: UpdateStepRequest;
        stepId: string;
        version: number;
        workflowId: string;
      }
    >({
      invalidatesTags: ['OrchestratorWorkflow'],
      query: ({ request, stepId, version, workflowId }) => ({
        body: request,
        method: 'PUT',
        url: `${BASE_URL}/workflows/${workflowId}/version/${version}/step/${stepId}`,
      }),
    }),
    updateTransitions: builder.mutation<
      OrchestratorWorkflow,
      {
        stepId: string;
        transitions: Array<{
          condition_expression?: unknown;
          step_id?: string | null;
          transition_id: string;
        }>;
        version: number;
        workflowId: string;
      }
    >({
      invalidatesTags: ['OrchestratorWorkflow'],
      query: ({ stepId, transitions, version, workflowId }) => ({
        body: { transitions },
        method: 'PUT',
        url: `${BASE_URL}/workflows/${workflowId}/version/${version}/step/${stepId}/transitions`,
      }),
    }),
    updateTrigger: builder.mutation<
      OrchestratorWorkflow,
      { request: SetTriggerConfigRequest; version: number; workflowId: string }
    >({
      invalidatesTags: ['OrchestratorWorkflow'],
      query: ({ request, version, workflowId }) => ({
        body: request,
        method: 'PUT',
        url: `${BASE_URL}/workflows/${workflowId}/versions/${version}/trigger`,
      }),
    }),
    validateWorkflow: builder.query<
      ValidationResponse,
      { version: number; workflowId: string }
    >({
      providesTags: ['OrchestratorWorkflow'],
      query: ({ version, workflowId }) => ({
        method: 'POST',
        url: `${BASE_URL}/workflows/${workflowId}/version/${version}/validate`,
      }),
    }),
  }),
});

export const {
  useAddInternalStepMutation,
  useAddStepMutation,
  useArchiveWorkflowMutation,
  useCloneWorkflowMutation,
  useCopyStepsMutation,
  useCreateWorkflowMutation,
  useDeleteInternalStepMutation,
  useDeleteStepMutation,
  useDiscardDraftMutation,
  useGetOrchestratorAnalyticsQuery,
  useGetOrchestratorApiTokenQuery,
  useGetOrchestratorTriageConfigsQuery,
  useGetRedactionConfigQuery,
  useGetVoicePhoneNumbersQuery,
  useGetWorkflowQuery,
  useLazyValidateWorkflowQuery,
  useListWorkflowsQuery,
  usePasteStepsMutation,
  usePublishWorkflowMutation,
  useSetWorkflowActiveMutation,
  useUpdateOrchestratorWorkflowMutation,
  useUpdateStepMutation,
  useUpdateTransitionsMutation,
  useUpdateTriggerMutation,
  useValidateWorkflowQuery,
} = orchestratorApi;
